/**
 * Pure transform for the header market-indicator pill (issue #266).
 *
 * Takes the user's saved settings + the four parallel data fetches the
 * Navbar component issues each 60s, and returns the render-ready chip
 * list. Kept pure so vitest can exercise the matching / fallback logic
 * without a React renderer or polling timer.
 *
 * The Navbar inlines the JSX itself — this only owns the data->view
 * shape, not the styling.
 */
import type {
  HeaderMarketIndicatorsSettings,
  TrendSummary,
} from './api-client';

export interface BatchStockLike {
  ticker: string;
  data?: { changePct?: number | null } | null;
}

export interface CommodityLike {
  symbol: string;
  changePct?: number | null;
}

export type HeaderChipKind = 'stock' | 'commodity';

export interface HeaderChip {
  kind: HeaderChipKind;
  /** Display label — uppercased ticker (e.g. 'SPY') or 'OIL' for CL=F. */
  label: string;
  /** Daily change in percent. Null while the first poll hasn't landed
   *  or the upstream provider returned no row. The consumer renders
   *  a "—" placeholder for null so chip width stays stable. */
  changePct: number | null;
  /** Where the chip links. Stocks → /stocks/<symbol>; OIL → the
   *  commodities listing page (same as the pre-#266 hardcoded chip). */
  href: string;
  /** 30-min trend summary from getWatchlistTrends. Always null for the
   *  OIL chip (no intraday bars for CL=F futures). Null for a stock
   *  means either the endpoint failed or there isn't enough history
   *  (early-session, weekends, idle PM/AH ticker) — render no arrow. */
  trend: TrendSummary | null;
}

/** Build the chip list the Navbar will render. Order: configured stock
 *  tickers in saved order, then the OIL chip last when `includeOil`.
 *
 *  Matching rules:
 *  - Stock lookup is case-insensitive: settings keep tickers uppercase,
 *    but `BatchStockResult.ticker` from getStocksBatch can echo back in
 *    whatever case the API returned, so we toUpperCase() both sides.
 *  - Commodity lookup is hardcoded to CL=F today. If a future revision
 *    adds GOLD/NATGAS checkboxes, extend this map and the Settings shape
 *    together.
 *  - When `settings.enabled === false`, returns []. Same when there's
 *    nothing to show (zero stocks AND includeOil=false). */
export function buildHeaderChips(
  settings: HeaderMarketIndicatorsSettings,
  batch: BatchStockLike[],
  commodities: CommodityLike[],
  trends: Record<string, TrendSummary | null>,
): HeaderChip[] {
  if (!settings.enabled) return [];
  const chips: HeaderChip[] = [];
  const batchByTicker = new Map<string, BatchStockLike>();
  for (const row of batch) {
    if (row && typeof row.ticker === 'string') {
      batchByTicker.set(row.ticker.toUpperCase(), row);
    }
  }
  for (const t of settings.tickers) {
    const up = t.toUpperCase();
    const row = batchByTicker.get(up);
    const pct = row?.data?.changePct;
    chips.push({
      kind: 'stock',
      label: up,
      changePct: typeof pct === 'number' && Number.isFinite(pct) ? pct : null,
      href: `/stocks/${encodeURIComponent(up)}`,
      trend: trends?.[up] ?? null,
    });
  }
  if (settings.includeOil) {
    const oilRow = commodities.find((c) => c.symbol === 'CL=F');
    const pct = oilRow?.changePct;
    chips.push({
      kind: 'commodity',
      label: 'OIL',
      changePct: typeof pct === 'number' && Number.isFinite(pct) ? pct : null,
      href: '/markets/commodities',
      trend: null,
    });
  }
  return chips;
}

/** Format a percent value for chip display: '+1.03%' / '-0.42%'. */
export function formatChipPct(v: number): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
}

/** Color class for a percent value. Null → muted placeholder. */
export function chipColorClass(v: number | null): string {
  if (v == null) return 'text-fg-subtle';
  return v >= 0 ? 'text-[#22c55e]' : 'text-[#ef4444]';
}
